import { createContext, type Dispatch, type FC, type ReactNode, type SetStateAction, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { localStorageService } from "../../services/LocalStorageService";
import { type Department, useAuth } from "../AuthProvider";
import { DEFAULT_CHOSEN_DEPARTMENT, EXCLUDED_ROUTES_FROM_DEPT_ID_REDIRECT } from "./consts";
import { useAdoptDepartment } from "./useAdoptDepartment";

export type ChosenDepartmentCtxValues = {
	chosenDepartment: Department;
	setChosenDepartment: Dispatch<SetStateAction<Department>>;
	/** Mints the department's token ahead of a switch: true to go ahead, false if superseded; throws when it cannot be opened. */
	prepareDepartment: (department: Department) => Promise<boolean>;
};
export const ChoseDepartmentContext = createContext<ChosenDepartmentCtxValues | null>(null);

export const ChoseDepartmentProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { isLoggedIn, userInfo } = useAuth();
	const [chosenDepartment, setChosenDepartment] = useState<Department>(DEFAULT_CHOSEN_DEPARTMENT);
	const { adoptDepartment, prepareDepartment } = useAdoptDepartment(chosenDepartment.department_id, setChosenDepartment);

	// biome-ignore lint/correctness/useExhaustiveDependencies: re-runs when the chosen department changes, so code that sets another department directly is brought back to the URL's.
	useEffect(() => {
		if (isLoggedIn && userInfo?.departments?.length) {
			const { pathname: currentPath, search: searchParams, hash } = location;

			const pathParts = currentPath.split("/").filter(Boolean);
			const facilityId = pathParts[0];

			// Check if a string is a valid departmentId
			const isDepartmentId = (segment: string): boolean => {
				return userInfo.departments?.some((dept) => dept.department_id === segment) ?? false;
			};

			// Check if route is excluded or already has departmentId
			const isExcluded = EXCLUDED_ROUTES_FROM_DEPT_ID_REDIRECT.some((route) => currentPath === route || currentPath.startsWith(`${route}/`));
			const firstSegment = pathParts[0];
			const hasDepartmentId = firstSegment && isDepartmentId(firstSegment);

			// If route needs redirect (not excluded and doesn't have departmentId)
			if (!isExcluded && !hasDepartmentId) {
				// Get departmentId for redirect
				let departmentIdForRedirect: string | null = null;

				// First try localStorage
				const storedDepartmentId = localStorageService.getChosenDepartmentID();
				if (storedDepartmentId && userInfo.departments?.some((dept) => dept.department_id === storedDepartmentId)) {
					departmentIdForRedirect = storedDepartmentId;
				}

				// Fall back to first department
				if (!departmentIdForRedirect) {
					departmentIdForRedirect = userInfo.departments?.[0]?.department_id || null;
				}

				// Redirect to new path with departmentId
				if (departmentIdForRedirect) {
					const newPath = `/${departmentIdForRedirect}${currentPath}${searchParams}${hash}`;
					navigate(newPath, { replace: true });
					return;
				}
			}

			let departmentToSet;
			if (facilityId) {
				departmentToSet = userInfo.departments.find((dept) => dept.department_id === facilityId);
			}

			// If not found in URL, fall back to localStorage
			if (!departmentToSet) {
				const storedDepartmentId = localStorageService.getChosenDepartmentID();
				departmentToSet = userInfo.departments.find((dept) => dept.department_id === storedDepartmentId);
			}

			// If still not found, use first department
			if (!departmentToSet) {
				departmentToSet = userInfo.departments[0];
			}

			if (departmentToSet) {
				adoptDepartment(departmentToSet);
			}
		}
	}, [isLoggedIn, userInfo, location, chosenDepartment.department_id, navigate, adoptDepartment]);

	return <ChoseDepartmentContext.Provider value={{ chosenDepartment, setChosenDepartment, prepareDepartment }}>{children}</ChoseDepartmentContext.Provider>;
};
